import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, OpenInNew, Save, Send, TaskAlt } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useClients } from "utils/clientService";
import { formatDisplayDateTime } from "utils/dateFormat";
import {
  completeAdminDocumentPacket,
  copyDocumentTemplateFields,
  createDocumentDraft,
  createDocumentTemplate,
  deleteDocumentTemplate,
  getDocumentTemplate,
  importDocumentTemplateFields,
  listDocumentPackets,
  listDocumentTemplates,
  openDocumentPacketPdf,
  sendDocumentPacket,
  updateDocumentDraft,
  updateDocumentTemplateFields,
  updateDocumentTemplateMaraRequirement,
  updateDocumentTemplateScopes,
} from "utils/documentService";
import { applyCheckboxChange } from "utils/exclusiveCheckboxes";
import { SCOPE_OPTIONS } from "components/Mara/MaraAgentFormDialog";
import PdfFieldCanvas from "components/DocumentMapping/PdfFieldCanvas";
import TemplateFieldList from "./TemplateFieldList";
import TemplateProtectionToggles from "./TemplateProtectionToggles";
import ReissueSignedCopiesButton from "./ReissueSignedCopiesButton";
import PdfProtectionNote from "components/Documents/PdfProtectionNote";

import {
  AUTOFILL_OWNER,
  LINKED_ONLY_OWNERS,
  fieldTypeForPrefillKey,
  withAutofillApplied,
} from "./documentFieldOptions";

const emptyUploadForm = { name: "", formType: "", description: "", file: null };
const scopeLabel = (tag) => SCOPE_OPTIONS.find((option) => option.value === tag)?.label || tag;

export default function Documents() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const initialClient = location.state?.client;
  const initialTemplateId = location.state?.templateId;
  const signatureCanvasRef = useRef(null);
  const drawingRef = useRef(false);

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateFields, setTemplateFields] = useState([]);
  const [uploadForm, setUploadForm] = useState(emptyUploadForm);
  const [selectedClientId, setSelectedClientId] = useState(initialClient?.id || "");
  const [draftId, setDraftId] = useState(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftEmail, setDraftEmail] = useState(initialClient?.email || "");
  const [fieldValues, setFieldValues] = useState({});
  const [assetUrls, setAssetUrls] = useState({});
  const [sendMessage, setSendMessage] = useState("");
  const [packetStatus, setPacketStatus] = useState("DRAFT");
  const [activePageNumber, setActivePageNumber] = useState(1);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signatureFieldName, setSignatureFieldName] = useState("");
  const [downloadingPacketKey, setDownloadingPacketKey] = useState(null);
  const [activeScope, setActiveScope] = useState(null);
  const [copySourceScope, setCopySourceScope] = useState("");
  const [copyResult, setCopyResult] = useState(null); // { copiedLabels, skippedOverlapLabels, fromLabel, toLabel }
  const [importFieldsConfirmOpen, setImportFieldsConfirmOpen] = useState(false);
  // react-query refetches this template whenever the window regains focus. Re-seeding the editor
  // from that response threw away every unsaved rectangle the moment somebody switched to another
  // tab and came back, so server data is only allowed to overwrite local state when what is being
  // edited actually changed: a different template or scope, or a mutation that rewrote the field
  // set server-side (which calls reloadFieldsFromServer explicitly).
  const syncedFieldsKeyRef = useRef(null);
  const syncedDraftKeyRef = useRef(null);

  const { data: clients = [] } = useClients({ enabled: true });
  const selectedClient = useMemo(
    () => clients.find((client) => String(client.id) === String(selectedClientId)),
    [clients, selectedClientId]
  );

  const templatesQuery = useQuery({
    queryKey: ["document-templates"],
    queryFn: listDocumentTemplates,
  });

  const selectedTemplateQuery = useQuery({
    queryKey: ["document-template", selectedTemplateId],
    queryFn: () => getDocumentTemplate(selectedTemplateId),
    enabled: !!selectedTemplateId,
  });

  const packetsQuery = useQuery({
    queryKey: ["document-packets", selectedClientId || "all"],
    queryFn: () => listDocumentPackets(selectedClientId || undefined),
  });

  useEffect(() => {
    if (!selectedTemplateId && templatesQuery.data?.length) {
      setSelectedTemplateId(initialTemplateId || templatesQuery.data[0].id);
    }
  }, [selectedTemplateId, templatesQuery.data, initialTemplateId]);

  useEffect(() => {
    if (selectedTemplateQuery.data) {
      const scopes = selectedTemplateQuery.data.scopes || [];
      setActiveScope((current) =>
        current && scopes.includes(current) ? current : scopes[0] || null
      );
    }
  }, [selectedTemplateQuery.data]);

  const applyServerFields = useCallback((template, scope) => {
    if (!template?.fields) return;
    syncedFieldsKeyRef.current = `${template.id}|${scope}`;
    const scoped = template.fields.filter((field) => !field.scope || field.scope === scope);
    setTemplateFields(scoped.map(withAutofillApplied));
    setActivePageNumber(1);
    setSelectedFieldIndex(null);
  }, []);

  useEffect(() => {
    const template = selectedTemplateQuery.data;
    if (!template?.fields) return;
    // Same template, same scope: this is a background refetch, so whatever is on screen wins.
    if (syncedFieldsKeyRef.current === `${template.id}|${activeScope}`) return;
    applyServerFields(template, activeScope);
  }, [selectedTemplateQuery.data, activeScope, applyServerFields]);

  /**
   * Pulls the server's field set back into the editor after a mutation rewrote it. Fetching
   * explicitly rather than leaning on the invalidation keeps this race-free: the editor is only
   * re-seeded once the fresh copy has actually arrived.
   */
  const reloadFieldsFromServer = async () => {
    if (!selectedTemplateId) return;
    const fresh = await queryClient.fetchQuery({
      queryKey: ["document-template", selectedTemplateId],
      queryFn: () => getDocumentTemplate(selectedTemplateId),
    });
    applyServerFields(fresh, activeScope);
  };

  // Same guard as the field editor: a focus refetch must not overwrite a title, an email or a
  // value the admin has already typed into the draft.
  useEffect(() => {
    const template = selectedTemplateQuery.data;
    if (!template || !selectedClient || draftId) return;
    const syncKey = `${template.id}|${selectedClient.id}`;
    if (syncedDraftKeyRef.current === syncKey) return;
    syncedDraftKeyRef.current = syncKey;
    setDraftTitle(`${template.name} - ${selectedClient.name}`);
    setDraftEmail(selectedClient.email || "");
    setFieldValues((current) =>
      applyLinkedClientValues(current, template.fields || [], selectedClient)
    );
  }, [selectedTemplateQuery.data, selectedClient, draftId]);

  const uploadTemplateMutation = useMutation({
    mutationFn: createDocumentTemplate,
    onSuccess: (data) => {
      toast.success("Template uploaded");
      setUploadForm(emptyUploadForm);
      setSelectedTemplateId(data.id);
      queryClient.invalidateQueries({ queryKey: ["document-templates"] });
    },
    onError: () => toast.error("Failed to upload template"),
  });

  const importFieldsMutation = useMutation({
    mutationFn: importDocumentTemplateFields,
    onSuccess: (response) => {
      const count = response?.fields?.length ?? 0;
      toast.success(
        count ? `Imported ${count} field(s) from the PDF` : "No fillable fields found in this PDF"
      );
      queryClient.invalidateQueries({ queryKey: ["document-template", selectedTemplateId] });
      reloadFieldsFromServer();
    },
    onError: () => toast.error("Failed to import fields from the PDF"),
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: deleteDocumentTemplate,
    onSuccess: () => {
      toast.success("Template deleted");
      setSelectedTemplateId(null);
      setTemplateFields([]);
      queryClient.invalidateQueries({ queryKey: ["document-templates"] });
    },
    onError: () => toast.error("Failed to delete template"),
  });

  const updateMaraRequirementMutation = useMutation({
    mutationFn: updateDocumentTemplateMaraRequirement,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["document-templates"] }),
    onError: () => toast.error("Failed to update MARA requirement"),
  });

  const updateScopesMutation = useMutation({
    mutationFn: updateDocumentTemplateScopes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document-template", selectedTemplateId] });
      queryClient.invalidateQueries({ queryKey: ["document-templates"] });
      reloadFieldsFromServer();
    },
    onError: () => toast.error("Failed to update module tags"),
  });

  const saveFieldsMutation = useMutation({
    mutationFn: ({ templateId, scope, fields }) =>
      updateDocumentTemplateFields(templateId, scope, fields),
    onSuccess: () => {
      toast.success("Template fields saved");
      queryClient.invalidateQueries({ queryKey: ["document-template", selectedTemplateId] });
      queryClient.invalidateQueries({ queryKey: ["document-templates"] });
      reloadFieldsFromServer();
    },
    onError: () => toast.error("Failed to save template fields"),
  });

  const copyFieldsMutation = useMutation({
    mutationFn: copyDocumentTemplateFields,
    onSuccess: (data, variables) => {
      setCopyResult({
        copiedLabels: data.copiedLabels,
        skippedOverlapLabels: data.skippedOverlapLabels,
        fromLabel: scopeLabel(variables.fromScope),
        toLabel: scopeLabel(variables.toScope),
      });
      setCopySourceScope("");
      queryClient.invalidateQueries({ queryKey: ["document-template", selectedTemplateId] });
      queryClient.invalidateQueries({ queryKey: ["document-templates"] });
      reloadFieldsFromServer();
    },
    onError: () => toast.error("Failed to copy field mapping"),
  });

  const saveDraftMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        templateId: selectedTemplateId,
        clientId: Number(selectedClientId),
        title: draftTitle,
        clientEmail: draftEmail,
        fieldValues,
      };
      return draftId
        ? updateDocumentDraft({ packetId: draftId, payload })
        : createDocumentDraft(payload);
    },
    onSuccess: (data) => applyLoadedPacket(data, false),
    onError: () => toast.error("Failed to save draft"),
  });

  const sendPacketMutation = useMutation({
    mutationFn: async () => {
      let packetId = draftId;
      if (!packetId) {
        const draft = await saveDraftMutation.mutateAsync();
        packetId = draft.id;
      }
      return sendDocumentPacket({ packetId, message: sendMessage });
    },
    onSuccess: (data) => {
      applyLoadedPacket(data, false);
      toast.success("Client email sent");
      queryClient.invalidateQueries({ queryKey: ["document-packets"] });
    },
    onError: () => toast.error("Failed to send signing request"),
  });

  const completeAdminMutation = useMutation({
    mutationFn: () => completeAdminDocumentPacket({ packetId: draftId, fieldValues }),
    onSuccess: (data) => {
      applyLoadedPacket(data, false);
      toast.success("Document completed");
      queryClient.invalidateQueries({ queryKey: ["document-packets"] });
    },
    onError: () => toast.error("Failed to complete admin signing"),
  });

  const selectedTemplate = selectedTemplateQuery.data;
  const previewImages = selectedTemplate?.previewImageUrls || [];
  const activePageImage = previewImages[activePageNumber - 1];
  const currentRole = packetStatus === "CLIENT_SIGNED" ? "ADMIN_POST_SIGN" : "ADMIN_PRE_SEND";
  const editableFields = templateFields.filter(
    (field) =>
      field.signerRole === currentRole ||
      // Admin's chance to spare the client the question; leaving it blank hands it over instead.
      (currentRole === "ADMIN_PRE_SEND" && field.signerRole === "CLIENT_IF_NOT_ADMIN_FILLED") ||
      (packetStatus === "CLIENT_SIGNED" &&
        field.signerRole === "ADMIN_IF_NOT_CLIENT_FILLED" &&
        !String(fieldValues[field.name] || "").trim())
  );

  const templateScopes = selectedTemplate?.scopes || [];
  const fieldsForScope = (tag) =>
    (selectedTemplate?.fields || []).filter((field) => (field.scope || null) === tag);
  const copySourceCandidates = templateScopes.filter(
    (tag) => tag !== activeScope && fieldsForScope(tag).length > 0
  );

  /** Only a checkbox can belong to a mutually exclusive set, so switching the type away drops the
   * group rather than leaving it dormant on the field. */
  const handleFieldTypeChange = (index, fieldType) => {
    setTemplateFields((current) =>
      current.map((field, fieldIndex) =>
        fieldIndex === index
          ? {
              ...field,
              fieldType,
              exclusiveGroup: fieldType === "CHECKBOX" ? field.exclusiveGroup || "" : "",
            }
          : field
      )
    );
  };

  const handleTemplateFieldChange = (index, key, value) => {
    setTemplateFields((current) =>
      current.map((field, fieldIndex) =>
        fieldIndex === index ? { ...field, [key]: value } : field
      )
    );
  };

  const handleAddField = () => {
    setTemplateFields((current) => {
      const next = [
        ...current,
        {
          name: `field_${current.length + 1}`,
          label: `Field ${current.length + 1}`,
          fieldType: "TEXT",
          signerRole: "CLIENT",
          pdfFieldName: "",
          pageNumber: activePageNumber,
          xPct: 0.1,
          yPct: 0.1,
          widthPct: 0.2,
          heightPct: 0.05,
          required: false,
          prefillKey: "",
          exclusiveGroup: "",
          visibleWhen: "",
          defaultValue: "",
          sortOrder: current.length,
        },
      ];
      setSelectedFieldIndex(next.length - 1);
      return next;
    });
  };

  const handlePrefillKeyChange = (index, prefillKey) => {
    setTemplateFields((current) =>
      current.map((field, fieldIndex) => {
        if (fieldIndex !== index) return field;
        if (!prefillKey) {
          return {
            ...field,
            prefillKey: "",
            signerRole: LINKED_ONLY_OWNERS.includes(field.signerRole)
              ? "ADMIN_PRE_SEND"
              : field.signerRole,
          };
        }
        return {
          ...field,
          prefillKey,
          fieldType: fieldTypeForPrefillKey(prefillKey),
          signerRole: AUTOFILL_OWNER,
        };
      })
    );
  };

  const handleRemoveField = (index) => {
    setTemplateFields((current) => current.filter((_, fieldIndex) => fieldIndex !== index));
    setSelectedFieldIndex(null);
  };

  const handleRectangleDrawn = (rect) => {
    if (selectedFieldIndex === null) return;
    setTemplateFields((current) =>
      current.map((field, index) =>
        index === selectedFieldIndex
          ? {
              ...field,
              pageNumber: rect.pageNumber,
              xPct: rect.xPct,
              yPct: rect.yPct,
              widthPct: rect.widthPct,
              heightPct: rect.heightPct,
            }
          : field
      )
    );
  };

  const handleFileFieldChange = async (fieldName, file) => {
    if (!file) return;
    const dataUrl = await readFileAsDataUrl(file);
    setFieldValues((current) => ({ ...current, [fieldName]: dataUrl }));
    setAssetUrls((current) => ({ ...current, [fieldName]: dataUrl }));
  };

  const openSignatureDialog = (fieldName) => {
    setSignatureFieldName(fieldName);
    setSignatureDialogOpen(true);
    requestAnimationFrame(() => clearSignatureCanvas());
  };

  const applySignatureToField = () => {
    const dataUrl = signatureCanvasRef.current.toDataURL("image/png");
    setFieldValues((current) => ({ ...current, [signatureFieldName]: dataUrl }));
    setAssetUrls((current) => ({ ...current, [signatureFieldName]: dataUrl }));
    setSignatureDialogOpen(false);
  };

  const handlePacketSelect = (packet) => {
    setDraftId(packet.id);
    setSelectedTemplateId(packet.templateId);
    setSelectedClientId(String(packet.clientId));
    setDraftTitle(packet.title);
    setDraftEmail(packet.clientEmail);
    setFieldValues(packet.fieldValues || {});
    setAssetUrls(packet.assetUrls || {});
    setSendMessage(packet.clientMessage || "");
    setPacketStatus(packet.status);
  };

  const handleDownloadPacketPdf = async (packet, variant) => {
    const key = `${packet.id}-${variant}`;
    setDownloadingPacketKey(key);
    try {
      await openDocumentPacketPdf(packet.id, variant);
    } catch (err) {
      toast.error("Failed to open PDF");
    } finally {
      setDownloadingPacketKey(null);
    }
  };

  return (
    <div className="w-full px-4 py-6">
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Document Center
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Map fields visually, link them to client details, prepare admin fields, then send or
            finalize after the client signs.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={4}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Upload Template
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Template name"
                    value={uploadForm.name}
                    onChange={(event) =>
                      setUploadForm((current) => ({ ...current, name: event.target.value }))
                    }
                    fullWidth
                  />
                  <TextField
                    label="Form type"
                    value={uploadForm.formType}
                    onChange={(event) =>
                      setUploadForm((current) => ({ ...current, formType: event.target.value }))
                    }
                    fullWidth
                  />
                  <TextField
                    label="Description"
                    value={uploadForm.description}
                    multiline
                    minRows={3}
                    onChange={(event) =>
                      setUploadForm((current) => ({ ...current, description: event.target.value }))
                    }
                    fullWidth
                  />
                  <TextField
                    type="file"
                    inputProps={{ accept: "application/pdf" }}
                    onChange={(event) =>
                      setUploadForm((current) => ({
                        ...current,
                        file: event.target.files?.[0] || null,
                      }))
                    }
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    disabled={!uploadForm.name || !uploadForm.file}
                    onClick={() => uploadTemplateMutation.mutate(uploadForm)}
                  >
                    Upload PDF Template
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 4, mt: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Templates
                </Typography>
                <Stack spacing={1.5}>
                  {(templatesQuery.data || []).map((template) => (
                    <Box
                      key={template.id}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor:
                          String(template.id) === String(selectedTemplateId)
                            ? "primary.main"
                            : "divider",
                        backgroundColor:
                          String(template.id) === String(selectedTemplateId)
                            ? "rgba(25,118,210,0.06)"
                            : "background.paper",
                        cursor: "pointer",
                      }}
                      onClick={() => setSelectedTemplateId(template.id)}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography fontWeight={700}>{template.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {template.formType || "General form"}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${template.fields?.length || 0} fields`}
                            sx={{ mt: 0.75 }}
                          />
                          <ReissueSignedCopiesButton template={template} />
                        </Box>
                        <Tooltip title="Requires a MARA agent to be selected when sending">
                          <FormControlLabel
                            sx={{ mr: 0 }}
                            onClick={(event) => event.stopPropagation()}
                            control={
                              <Switch
                                size="small"
                                checked={Boolean(template.maraRequired)}
                                disabled={updateMaraRequirementMutation.isLoading}
                                onChange={(event) =>
                                  updateMaraRequirementMutation.mutate({
                                    templateId: template.id,
                                    maraRequired: event.target.checked,
                                  })
                                }
                              />
                            }
                            label={<Typography variant="caption">MARA</Typography>}
                            labelPlacement="top"
                          />
                        </Tooltip>
                        <TemplateProtectionToggles template={template} />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Template Mapping
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Draw rectangles on the preview and assign field type, ownership, and linked
                      client detail.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    {selectedTemplate?.templateFileUrl && (
                      <Button
                        variant="outlined"
                        startIcon={<OpenInNew />}
                        onClick={() => window.open(selectedTemplate.templateFileUrl, "_blank")}
                      >
                        Open PDF
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      disabled={!selectedTemplateId || importFieldsMutation.isLoading}
                      onClick={() => setImportFieldsConfirmOpen(true)}
                    >
                      {importFieldsMutation.isLoading ? "Importing…" : "Import Fields"}
                    </Button>
                    <Button variant="contained" startIcon={<Add />} onClick={handleAddField}>
                      Add Field
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={!selectedTemplateId}
                      onClick={() =>
                        window.confirm("Delete this template?") &&
                        deleteTemplateMutation.mutate(selectedTemplateId)
                      }
                    >
                      Delete Template
                    </Button>
                  </Stack>
                </Stack>

                {selectedTemplate && (
                  <Stack spacing={1.5} sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        Used by modules (optional — same document, mapped separately per module)
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                        {SCOPE_OPTIONS.map((option) => {
                          const active = templateScopes.includes(option.value);
                          return (
                            <Chip
                              key={option.value}
                              size="small"
                              clickable
                              label={option.label}
                              color={active ? "primary" : "default"}
                              variant={active ? "filled" : "outlined"}
                              onClick={() =>
                                updateScopesMutation.mutate({
                                  templateId: selectedTemplateId,
                                  scopes: active
                                    ? templateScopes.filter((tag) => tag !== option.value)
                                    : [...templateScopes, option.value],
                                })
                              }
                            />
                          );
                        })}
                      </Stack>
                    </Box>

                    {templateScopes.length > 0 && (
                      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                          Mapping for:
                        </Typography>
                        {templateScopes.map((tag) => (
                          <Button
                            key={tag}
                            size="small"
                            variant={activeScope === tag ? "contained" : "outlined"}
                            onClick={() => setActiveScope(tag)}
                          >
                            {scopeLabel(tag)}
                          </Button>
                        ))}
                        {copySourceCandidates.length > 0 && (
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 1 }}>
                            {copySourceCandidates.length > 1 && (
                              <TextField
                                select
                                size="small"
                                label="Copy from"
                                value={copySourceScope}
                                onChange={(event) => setCopySourceScope(event.target.value)}
                                sx={{ minWidth: 160 }}
                              >
                                {copySourceCandidates.map((tag) => (
                                  <MenuItem key={tag} value={tag}>
                                    {scopeLabel(tag)}
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={
                                (copySourceCandidates.length > 1 && !copySourceScope) ||
                                copyFieldsMutation.isLoading
                              }
                              onClick={() =>
                                copyFieldsMutation.mutate({
                                  templateId: selectedTemplateId,
                                  fromScope:
                                    copySourceCandidates.length > 1
                                      ? copySourceScope
                                      : copySourceCandidates[0],
                                  toScope: activeScope,
                                })
                              }
                            >
                              Copy mapping from{" "}
                              {copySourceCandidates.length === 1
                                ? scopeLabel(copySourceCandidates[0])
                                : "…"}
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                    )}
                  </Stack>
                )}

                {!selectedTemplate ? (
                  <Alert severity="info">Select a template to start mapping fields.</Alert>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12} xl={7}>
                      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
                        {previewImages.map((_, index) => (
                          <Button
                            key={index}
                            variant={activePageNumber === index + 1 ? "contained" : "outlined"}
                            onClick={() => setActivePageNumber(index + 1)}
                          >
                            Page {index + 1}
                          </Button>
                        ))}
                      </Stack>
                      <PdfFieldCanvas
                        pageImageUrl={activePageImage}
                        fields={templateFields}
                        activePageNumber={activePageNumber}
                        selectedFieldIndex={selectedFieldIndex}
                        onSelectField={setSelectedFieldIndex}
                        onRectangleDrawn={handleRectangleDrawn}
                      />
                    </Grid>

                    <Grid item xs={12} xl={5}>
                      <TemplateFieldList
                        fields={templateFields}
                        selectedIndex={selectedFieldIndex}
                        onSelect={(index) => {
                          setSelectedFieldIndex(index);
                          setActivePageNumber(templateFields[index].pageNumber || 1);
                        }}
                        onChange={handleTemplateFieldChange}
                        onFieldTypeChange={handleFieldTypeChange}
                        onPrefillKeyChange={handlePrefillKeyChange}
                        onDelete={handleRemoveField}
                      />
                    </Grid>
                  </Grid>
                )}

                {selectedTemplate && (
                  <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={() =>
                        saveFieldsMutation.mutate({
                          templateId: selectedTemplateId,
                          scope: activeScope,
                          fields: templateFields.map((field, index) => ({
                            ...field,
                            sortOrder: index,
                          })),
                        })
                      }
                    >
                      Save Field Mapping
                    </Button>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ borderRadius: 4 }}>
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {packetStatus === "CLIENT_SIGNED"
                    ? "Finalize After Client Signature"
                    : "Prepare Document"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {packetStatus === "CLIENT_SIGNED"
                    ? "Fill or sign the post-client admin fields, then complete the document."
                    : "Fill the pre-send admin fields, upload photos if needed, and send to the client."}
                </Typography>
              </Box>
              <Chip
                label={packetStatus}
                color={packetStatus === "COMPLETED" ? "success" : "default"}
              />
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Client"
                  value={selectedClientId}
                  onChange={(event) => {
                    setSelectedClientId(event.target.value);
                    setDraftId(null);
                    syncedDraftKeyRef.current = null;
                    setPacketStatus("DRAFT");
                  }}
                  fullWidth
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Document title"
                  value={draftTitle}
                  onChange={(event) => setDraftTitle(event.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Client email"
                  value={draftEmail}
                  onChange={(event) => setDraftEmail(event.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              {editableFields.map((field) => (
                <Grid item xs={12} md={6} key={field.name}>
                  {field.fieldType === "PHOTO" ? (
                    <Stack spacing={1}>
                      <Typography fontWeight={600}>{field.label}</Typography>
                      {(assetUrls[field.name] || fieldValues[field.name]) && (
                        <img
                          src={assetUrls[field.name] || fieldValues[field.name]}
                          alt={field.label}
                          style={{
                            width: "100%",
                            maxHeight: 180,
                            objectFit: "contain",
                            borderRadius: 12,
                            border: "1px solid #cbd5e1",
                          }}
                        />
                      )}
                      <TextField
                        type="file"
                        inputProps={{ accept: "image/*" }}
                        onChange={(event) =>
                          handleFileFieldChange(field.name, event.target.files?.[0])
                        }
                        fullWidth
                      />
                    </Stack>
                  ) : field.fieldType === "SIGNATURE" ? (
                    <Stack spacing={1}>
                      <Typography fontWeight={600}>{field.label}</Typography>
                      {(assetUrls[field.name] || fieldValues[field.name]) && (
                        <img
                          src={assetUrls[field.name] || fieldValues[field.name]}
                          alt={field.label}
                          style={{
                            width: "100%",
                            maxHeight: 140,
                            objectFit: "contain",
                            borderRadius: 12,
                            border: "1px solid #cbd5e1",
                            background: "#fff",
                          }}
                        />
                      )}
                      <Button variant="outlined" onClick={() => openSignatureDialog(field.name)}>
                        {fieldValues[field.name] ? "Update Signature" : "Add Signature"}
                      </Button>
                    </Stack>
                  ) : field.fieldType === "CHECKBOX" ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={fieldValues[field.name] === "true"}
                          onChange={(event) =>
                            setFieldValues((current) =>
                              applyCheckboxChange(
                                templateFields,
                                current,
                                field,
                                event.target.checked
                              )
                            )
                          }
                        />
                      }
                      label={field.label}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      label={field.label}
                      value={fieldValues[field.name] || ""}
                      multiline={field.fieldType === "MULTILINE"}
                      minRows={field.fieldType === "MULTILINE" ? 3 : 1}
                      type={field.fieldType === "DATE" ? "date" : "text"}
                      InputLabelProps={field.fieldType === "DATE" ? { shrink: true } : undefined}
                      inputProps={field.combCells ? { maxLength: field.combCells } : undefined}
                      onChange={(event) =>
                        setFieldValues((current) => ({
                          ...current,
                          [field.name]: event.target.value,
                        }))
                      }
                    />
                  )}
                </Grid>
              ))}
            </Grid>

            {packetStatus !== "CLIENT_SIGNED" && (
              <TextField
                label="Client message"
                value={sendMessage}
                onChange={(event) => setSendMessage(event.target.value)}
                multiline
                minRows={3}
                fullWidth
                sx={{ mt: 3 }}
              />
            )}

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<Save />}
                onClick={() => saveDraftMutation.mutate()}
                disabled={!selectedTemplateId || !selectedClientId || !draftTitle || !draftEmail}
              >
                Save Draft
              </Button>
              {packetStatus === "CLIENT_SIGNED" ? (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<TaskAlt />}
                  onClick={() => completeAdminMutation.mutate()}
                  disabled={!draftId}
                >
                  Complete Document
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={() => sendPacketMutation.mutate()}
                  disabled={!selectedTemplateId || !selectedClientId || !draftTitle || !draftEmail}
                >
                  Send To Client
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 4 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Document Requests
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sent</TableCell>
                  <TableCell>Client Signed</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(packetsQuery.data || []).map((packet) => (
                  <TableRow key={packet.id}>
                    <TableCell>{packet.title}</TableCell>
                    <TableCell>{packet.clientName}</TableCell>
                    <TableCell>
                      <Chip
                        label={packet.status}
                        size="small"
                        color={
                          packet.status === "COMPLETED"
                            ? "success"
                            : packet.status === "CLIENT_SIGNED"
                            ? "warning"
                            : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>{formatDisplayDateTime(packet.sentAt, "-")}</TableCell>
                    <TableCell>{formatDisplayDateTime(packet.clientSignedAt, "-")}</TableCell>
                    <TableCell>{formatDisplayDateTime(packet.completedAt, "-")}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button size="small" onClick={() => handlePacketSelect(packet)}>
                          Open
                        </Button>
                        {packet.hasDraftPdf && (
                          <Button
                            size="small"
                            disabled={downloadingPacketKey === `${packet.id}-draft`}
                            onClick={() => handleDownloadPacketPdf(packet, "draft")}
                          >
                            Draft PDF
                          </Button>
                        )}
                        {packet.hasSignedPdf && (
                          <Button
                            size="small"
                            disabled={downloadingPacketKey === `${packet.id}-signed`}
                            onClick={() => handleDownloadPacketPdf(packet, "signed")}
                          >
                            Signed PDF
                          </Button>
                        )}
                        {packet.hasLegacySignedPdf && (
                          <Button
                            size="small"
                            disabled={downloadingPacketKey === `${packet.id}-legacy`}
                            onClick={() => handleDownloadPacketPdf(packet, "legacy")}
                          >
                            Earlier copy
                          </Button>
                        )}
                        {packet.publicSigningUrl && (
                          <Button
                            size="small"
                            onClick={() => window.open(packet.publicSigningUrl, "_blank")}
                          >
                            Public Link
                          </Button>
                        )}
                      </Stack>
                      {(packet.hasDraftPdf || packet.hasSignedPdf) && (
                        <Box sx={{ mt: 0.5 }}>
                          <PdfProtectionNote
                            protection={packet.signedPdfProtection}
                            password={packet.pdfOwnerPassword}
                          />
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Stack>

      <Dialog
        open={importFieldsConfirmOpen}
        onClose={() => setImportFieldsConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Import fields from this PDF?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Import fillable fields from this PDF's own form? This replaces the current (universal,
            non-module) field mapping with what's detected — draw/adjust manually afterward as
            needed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setImportFieldsConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setImportFieldsConfirmOpen(false);
              importFieldsMutation.mutate(selectedTemplateId);
            }}
          >
            Import Fields
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={signatureDialogOpen}
        onClose={() => setSignatureDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Draw Signature</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: "1px solid #cbd5e1",
              borderRadius: 3,
              overflow: "hidden",
              backgroundColor: "#fff",
            }}
          >
            <canvas
              ref={signatureCanvasRef}
              width={900}
              height={300}
              style={{ width: "100%", display: "block", touchAction: "none" }}
              onPointerDown={(event) => {
                const canvas = signatureCanvasRef.current;
                const rect = canvas.getBoundingClientRect();
                const context = canvas.getContext("2d");
                context.strokeStyle = "#0f172a";
                context.lineWidth = 2.5;
                context.lineCap = "round";
                context.beginPath();
                context.moveTo(event.clientX - rect.left, event.clientY - rect.top);
                drawingRef.current = true;
              }}
              onPointerMove={(event) => {
                if (!drawingRef.current) return;
                const canvas = signatureCanvasRef.current;
                const rect = canvas.getBoundingClientRect();
                const context = canvas.getContext("2d");
                context.lineTo(event.clientX - rect.left, event.clientY - rect.top);
                context.stroke();
              }}
              onPointerUp={() => {
                drawingRef.current = false;
              }}
              onPointerLeave={() => {
                drawingRef.current = false;
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={clearSignatureCanvas}>Clear</Button>
          <Button variant="contained" onClick={applySignatureToField}>
            Use Signature
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(copyResult)}
        onClose={() => setCopyResult(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Copied from {copyResult?.fromLabel} to {copyResult?.toLabel}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="info">
              Linked client detail was not copied — set it individually for each field on this
              module's mapping.
            </Alert>
            {copyResult?.copiedLabels.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Copied ({copyResult.copiedLabels.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {copyResult.copiedLabels.join(", ")}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nothing was copied.
              </Typography>
            )}
            {copyResult?.skippedOverlapLabels.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="warning.main">
                  Skipped — overlaps an existing field ({copyResult.skippedOverlapLabels.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {copyResult.skippedOverlapLabels.join(", ")}
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );

  function applyLoadedPacket(packet, preserveStatus) {
    setDraftId(packet.id);
    setDraftTitle(packet.title);
    setDraftEmail(packet.clientEmail);
    setFieldValues(packet.fieldValues || {});
    setAssetUrls(packet.assetUrls || {});
    setSendMessage(packet.clientMessage || "");
    setPacketStatus(preserveStatus ? packetStatus : packet.status);
  }

  function clearSignatureCanvas() {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
  }
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function applyLinkedClientValues(currentValues, fields, client) {
  const next = { ...currentValues };
  for (const field of fields) {
    if (!field.prefillKey || next[field.name]) continue;
    const value = resolveClientDetail(client, field.prefillKey);
    if (value) {
      next[field.name] = value;
    }
  }
  return next;
}

function resolveClientDetail(client, key) {
  switch (key) {
    case "client.name":
      return client?.name || "";
    case "client.email":
      return client?.email || "";
    case "client.number":
      return client?.number || "";
    case "client.nationality":
      return client?.nationality || "";
    case "client.passportNumber":
      return client?.passportNumber || "";
    case "client.visaStatus":
      return client?.visaStatus || "";
    case "client.usiNumber":
      return client?.usiNumber || "";
    case "client.address":
      return client?.address || client?.onshoreAddress || client?.homeAddress || "";
    case "client.address2":
      return client?.address2 || "";
    case "client.country":
      return client?.country || "";
    case "client.state":
      return client?.state || "";
    case "client.city":
      return client?.city || "";
    case "client.zipCode":
      return client?.zipCode || "";
    case "client.photo":
      return client?.image || "";
    default:
      return "";
  }
}
